import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'

import type {
    ShopMediaInput,
    ShopMediaListParams,
} from '@/lib/api/shop-media-api'
import { shopMediaApi } from '@/lib/api/shop-media-api'

export const shopMediaKeys = {
    all: ['shop-media'] as const,
    list: (params: Omit<ShopMediaListParams, 'page'>) =>
        [...shopMediaKeys.all, 'list', params] as const,
    usage: (id: string) => [...shopMediaKeys.all, 'usage', id] as const,
}

const PER_PAGE = 40

/** Lưới thư viện: tìm trên server (debounce ở màn), cuộn tới đáy tải trang sau. */
export function useShopMediaInfinite(
    params: Omit<ShopMediaListParams, 'page' | 'per_page'>,
    options?: { enabled?: boolean },
) {
    const query = { ...params, per_page: PER_PAGE }
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: shopMediaKeys.list(query),
        queryFn: ({ pageParam }) =>
            shopMediaApi.list({ ...query, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useShopMediaUsage(id: string | null) {
    return useQuery({
        enabled: !!id,
        queryKey: shopMediaKeys.usage(id ?? ''),
        queryFn: () => shopMediaApi.usage(id as string),
    })
}

// Thông báo kết quả (số file tải được / lỗi từng file) do màn gọi tự hiện.
export function useUploadShopMedia() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (files: File[]) => shopMediaApi.upload(files),
        onSettled: () => qc.invalidateQueries({ queryKey: shopMediaKeys.all }),
    })
}

export function useUpdateShopMedia() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: ShopMediaInput }) =>
            shopMediaApi.update(id, input),
        onSuccess: () => qc.invalidateQueries({ queryKey: shopMediaKeys.all }),
    })
}

export function useDeleteShopMedia() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => shopMediaApi.remove(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: shopMediaKeys.all }),
    })
}
